import {forwardRef, useRef} from "react";
import clsx from "clsx";
import {Input as UIInput} from "@headlessui/react";
import {CloseCircle} from "@chayka/icons";

interface InputProps extends React.ComponentPropsWithoutRef<"input"> {
  endAdornment?: React.ReactNode;
  clearable?: boolean;
}

const ClearButton: React.FC<{onClick: () => void}> = ({onClick}) => (
  <button type="button" onClick={onClick} className="ml-2 text-secondary-text focus:outline-none">
    <CloseCircle size={20} />
  </button>
);

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({endAdornment, clearable, className, disabled, ...props}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const assignRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === "function") {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    };

    const handleClear = () => {
      if (!inputRef.current) {
        return;
      }

      inputRef.current.value = "";
      inputRef.current.focus();
      inputRef.current.dispatchEvent(new Event("input", {bubbles: true}));
    };

    return (
      <div className="relative">
        <UIInput
          as="input"
          {...props}
          ref={assignRef}
          className={clsx(
            "w-full px-4 py-2 border rounded-md border-gray-300 focus:outline-none focus:border-black",
            endAdornment || clearable ? "pr-10" : "",
            disabled ? "bg-gray-100 text-inactive-text" : "",
            className,
          )}
          disabled={disabled}
        />
        {(endAdornment || clearable) && (
          <div className="absolute flex items-center justify-center right-3 top-1/2 transform -translate-y-1/2">
            {endAdornment ? endAdornment : clearable ? <ClearButton onClick={handleClear} /> : null}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
